import { BadRequestException, Injectable, Logger } from '@nestjs/common';
import { Cron } from '@nestjs/schedule';
import { PrismaService } from '@cart/api-data-access';
import { compressImage, isCompressible } from '../commerce-admin/image-compression.util';
import { MediaAssetRecord, MediaAssetService } from './media-asset.service';
import { MediaOwnerType, assertKeyBelongsTo, buildMediaKey, isPrivateOwnerType, sanitizeFileName } from './media-key.util';
import { ALLOWED_DOCUMENT_MIME_TYPES, isRejectedType, validateImageUpload } from './mime-sniff.util';
import { StorageAdapterResolver } from './storage-adapter-resolver.service';
import { StorageQuotaService } from './storage-quota.service';

/**
 * Images up to this size are proxied through the API; anything larger must use
 * the presigned path. The line is drawn where server-side compression stops
 * being worth the bandwidth, not at some architectural purity boundary.
 */
export const PROXIED_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;

/** A reservation that is never confirmed is swept after this long. */
const RESERVATION_TTL_MS = 60 * 60 * 1000;

export interface ProxiedUploadInput {
  organizationId: string;
  storeId?: string | null;
  ownerType: MediaOwnerType;
  ownerId?: string | null;
  /**
   * Set when the image belongs to one variant rather than the whole product.
   * Becomes a level in the object key; see media-key.util.ts.
   */
  variantId?: string | null;
  fileName: string;
  declaredMimeType: string;
  buffer: Buffer;
  compressionPercent?: number;
  actorId?: string;
}

export interface ReserveUploadInput {
  organizationId: string;
  storeId?: string | null;
  ownerType: MediaOwnerType;
  ownerId?: string | null;
  /**
   * Set when the image belongs to one variant rather than the whole product.
   * Becomes a level in the object key; see media-key.util.ts.
   */
  variantId?: string | null;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  actorId?: string;
}

/**
 * The two upload paths (docs/19-media-management-plan.md §8.1).
 *
 * **Proxied** for images ≤10 MB: the bytes reach the API, so `sharp`
 * compression, magic-byte validation and exact size accounting all still
 * work. Sending every upload straight to the bucket would have silently
 * disabled all three — a merchant on 80% compression would suddenly store 5×
 * more, and quota would be enforced against a size the client asserted.
 *
 * **Presigned reserve → confirm** for documents and large files: quota is
 * checked against the declared size, a `reserved` row holds that space, the
 * browser PUTs directly, and confirm replaces the declared size with the real
 * one read back from the provider. A client that lies about its size gets the
 * space it reserved and no more, because the signed URL carries a
 * content-length condition.
 */
@Injectable()
export class MediaUploadService {
  private readonly logger = new Logger(MediaUploadService.name);

  constructor(
    private readonly prisma: PrismaService,
    private readonly assets: MediaAssetService,
    private readonly quota: StorageQuotaService,
    private readonly resolver: StorageAdapterResolver
  ) {}

  async uploadProxied(input: ProxiedUploadInput): Promise<MediaAssetRecord & { quotaWarning: boolean }> {
    if (input.buffer.length > PROXIED_UPLOAD_MAX_BYTES) {
      throw new BadRequestException(
        `Files over ${PROXIED_UPLOAD_MAX_BYTES / (1024 * 1024)} MB must use the direct upload flow.`
      );
    }

    const validation = validateImageUpload(input.buffer, input.declaredMimeType, input.fileName);
    if (!validation.ok) throw new BadRequestException(validation.reason);

    let buffer = input.buffer;
    const compression = input.compressionPercent ?? 0;
    if (compression > 0 && isCompressible(validation.mimeType)) {
      const compressed = await compressImage(buffer, validation.mimeType, compression);
      buffer = compressed.buffer;
    }

    // storeId matters here: the merchant may have divided their pool between
    // stores, and a store can be full while the account is not.
    const { warning } = await this.quota.assertCanUpload(
      input.organizationId,
      buffer.length,
      input.storeId ?? null
    );

    const asset = await this.assets.write({
      organizationId: input.organizationId,
      storeId: input.storeId,
      ownerType: input.ownerType,
      ownerId: input.ownerId,
      variantId: input.variantId,
      fileName: input.fileName,
      // The sniffed type, never the declared one — that header is caller data.
      mimeType: validation.mimeType,
      buffer,
      actorId: input.actorId,
    });

    return { ...asset, quotaWarning: warning };
  }

  /**
   * Step 1 of the direct path. The reservation counts against quota while it
   * lives, so a merchant cannot reserve their way past the cap by never
   * confirming.
   */
  async reserve(input: ReserveUploadInput) {
    if (input.sizeBytes <= 0) throw new BadRequestException('sizeBytes is required');
    if (isRejectedType(input.mimeType, input.fileName)) {
      throw new BadRequestException('SVG and HTML uploads are not allowed.');
    }
    if (input.ownerType === 'document' && !ALLOWED_DOCUMENT_MIME_TYPES.includes(input.mimeType)) {
      throw new BadRequestException(`Unsupported document type ${input.mimeType}`);
    }

    await this.quota.assertCanUpload(input.organizationId, input.sizeBytes, input.storeId ?? null);

    const config = await this.assets.requireActiveConfig(input.organizationId);
    const { key, assetId } = buildMediaKey({
      organizationId: input.organizationId,
      ownerType: input.ownerType,
      ownerId: input.ownerId,
      variantId: input.variantId,
      storeId: input.storeId,
      mimeType: input.mimeType,
      fileName: input.fileName,
    });
    assertKeyBelongsTo(input.organizationId, key);

    const adapter = await this.resolver.forConfig(config, 'presign');
    // maxBytes is signed into the URL: without it the presigned PUT is an open
    // upload endpoint and the declared size means nothing.
    const upload = await adapter.createUploadUrl(key, input.mimeType, input.sizeBytes);

    // The reservation holds its declared bytes against the quota immediately.
    // Charging only on confirm would let a caller reserve unlimited space and
    // simply never confirm — the cap would be advisory.
    await this.prisma.$transaction(async tx => {
      await tx.mediaAsset.create({
        data: {
          id: assetId,
          organizationId: input.organizationId,
          storeId: input.storeId ?? null,
          ownerType: input.ownerType,
          ownerId: input.ownerId ?? null,
          storageConfigId: config.id,
          storageKey: key,
          publicUrl: null,
          fileName: sanitizeFileName(input.fileName),
          mimeType: input.mimeType,
          sizeBytes: input.sizeBytes,
          status: 'reserved',
          reservedUntil: new Date(Date.now() + RESERVATION_TTL_MS),
          createdBy: input.actorId ?? 'system',
          updatedBy: input.actorId ?? 'system',
        },
      });
      await this.assets.applyUsageDelta(tx, {
        organizationId: input.organizationId,
        configId: config.id,
        mode: config.mode,
        bytes: input.sizeBytes,
        count: 1,
        storeId: input.storeId ?? null,
      });
    });

    // The presigned URL is a bearer credential until it expires and carries an
    // access key ID in its query string — it is returned to the browser and
    // logged nowhere.
    return {
      assetId,
      upload: { url: upload.url, method: upload.method, headers: upload.headers, expiresAt: upload.expiresAt },
    };
  }

  /**
   * Step 3. The size is taken from the provider's HEAD response, not from
   * anything the client said — that is the whole point of confirming.
   */
  async confirm(assetId: string, organizationId: string, actorId = 'system'): Promise<MediaAssetRecord> {
    const asset = await this.assets.assertOwned(assetId, organizationId);
    if (asset.status !== 'reserved') {
      throw new BadRequestException('This upload has already been confirmed.');
    }

    const config = await this.resolver.configById(asset.storageConfigId);
    const adapter = await this.resolver.forConfig(config, 'upload');

    const meta = await adapter.getMetadata(asset.storageKey).catch(() => null);
    if (!meta) {
      throw new BadRequestException('No uploaded file found for this reservation.');
    }

    const declared = asset.sizeBytes;
    const actual = meta.sizeBytes;
    const delta = actual - declared;
    const publicUrl = isPrivateOwnerType(asset.ownerType) ? null : adapter.publicUrl(asset.storageKey);

    const updated = await this.prisma.$transaction(async tx => {
      const row = await tx.mediaAsset.update({
        where: { id: assetId },
        data: {
          status: 'active',
          sizeBytes: actual,
          publicUrl,
          reservedUntil: null,
          updatedBy: actorId,
        },
      });

      // Correction only — the declared size was already charged at reserve
      // time, so this moves the counters by the difference the provider
      // reported, in either direction, with the same zero-clamped helper.
      if (delta !== 0) {
        await this.assets.applyUsageDelta(tx, {
          organizationId,
          configId: config.id,
          mode: config.mode,
          bytes: delta,
          count: 0,
          storeId: asset.storeId,
        });
      }

      return row;
    });

    return {
      id: updated.id,
      storageKey: updated.storageKey,
      publicUrl: updated.publicUrl,
      sizeBytes: updated.sizeBytes,
      mimeType: updated.mimeType,
      fileName: updated.fileName,
      storageConfigId: updated.storageConfigId,
    };
  }

  /**
   * Sweeps reservations nobody confirmed.
   *
   * Promotes any whose object actually landed (the browser uploaded but the
   * confirm call was lost) and drops the rest, releasing their held quota.
   */
  @Cron('15 * * * *')
  async sweepExpiredReservations(): Promise<void> {
    const expired = await this.prisma.mediaAsset.findMany({
      where: { status: 'reserved', reservedUntil: { lt: new Date() } },
      select: {
        id: true,
        organizationId: true,
        storageConfigId: true,
        storageKey: true,
        sizeBytes: true,
        storeId: true,
      },
      take: 200,
    });
    if (!expired.length) return;

    let promoted = 0;
    let dropped = 0;

    for (const asset of expired) {
      try {
        const config = await this.resolver.configById(asset.storageConfigId);
        const adapter = await this.resolver.forConfig(config, 'upload');
        if (await adapter.exists(asset.storageKey)) {
          await this.confirm(asset.id, asset.organizationId);
          promoted += 1;
          continue;
        }

        await this.prisma.$transaction(async tx => {
          await tx.mediaAsset.delete({ where: { id: asset.id } });
          await this.assets.applyUsageDelta(tx, {
            organizationId: asset.organizationId,
            configId: asset.storageConfigId,
            mode: config.mode,
            bytes: -asset.sizeBytes,
            count: -1,
            storeId: asset.storeId,
          });
        });
        dropped += 1;
      } catch (err) {
        this.logger.warn(`Reservation sweep failed for ${asset.id}: ${err}`);
      }
    }

    this.logger.log(`Reservation sweep: ${promoted} promoted, ${dropped} released`);
  }
}
